// AssignmentForm.tsx
import * as React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Spinner from "@/components/Spinner";
import { Calendar } from "lucide-react";
import { AssignmentFormProps } from "@/lib/AssignmentFormTypes";

export default function AddAssignmentForm({
  form,
  allBatch,
  editingAssignment,
  creating,
  updating,
  handleChange,
  handleFile,
  handleSubmit,
  reset,
}: AssignmentFormProps) {
  return (
    <form className="space-y-4 mb-6" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            name="title"
            placeholder="Enter title"
            value={form.title}
            onChange={handleChange}
          />
        </div>

        {/* Type */}
        <div className="space-y-2">
          <Label htmlFor="type">Type</Label>
          <select
            id="type"
            name="type"
            className="border rounded-md px-2 py-2 w-full bg-background"
            value={form.type}
            onChange={handleChange}
          >
            <option value="assignment">Assignment</option>
            <option value="task">Task</option>
          </select>
        </div>

        {/* Batch */}
        <div className="space-y-2">
          <Label htmlFor="batch">Batch</Label>

          {editingAssignment ? (
            // READ-ONLY batch when editing
            <Input
              disabled
              value={editingAssignment.batchCode?.bCode || ""}
              className="bg-gray-200 cursor-not-allowed"
            />
          ) : (
            // SELECT batch when creating
            <select
              id="batch"
              name="batch"
              className="border rounded-md px-2 py-2 w-full bg-background"
              value={form.batch}
              onChange={handleChange}
            >
              <option value="">Select Batch</option>
              {allBatch?.map((b: Batch) => (
                <option key={b._id} value={b._id}>
                  {b.bCode}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Show only when type is task */}
        {form.type === "task" && (
          <div className="space-y-2 mt-2 flex flex-col">
            <Label htmlFor="link">Link</Label>
            <Input
              name="link"
              value={form.link}
              onChange={handleChange}
              placeholder="Paste task link here"
            />
          </div>
        )}
        {/* File Upload */}
        <div className="space-y-2">
          <Label htmlFor="file">Upload File</Label>
          {editingAssignment?.uploadedFile ? (
            <>
              <Input
                type="text"
                disabled
                value={editingAssignment.uploadedFile.split("/").pop()}
                className="bg-gray-200 cursor-not-allowed"
              />
              <a
                href={editingAssignment.uploadedFile}
                target="_blank"
                className="text-blue-600 underline text-sm"
              >
                View File
              </a>
            </>
          ) : (
            <Input id="file" name="file" type="file" onChange={handleFile} />
          )}
        </div>

        {/* Total Marks */}
        <div className="space-y-2">
          <Label>Total Marks</Label>
          <Input
            name="totalMarks"
            value={form.totalMarks}
            onChange={handleChange}
            placeholder="Enter Total Marks"
          />
        </div>

        {/* Deadline */}
        <div className="space-y-2">
          <Label>Deadline</Label>
          <div className="relative">
            <Input
              type="date"
              name="deadline"
              value={form.deadline}
              onChange={handleChange}
              className="pr-10"
            />
            <Calendar
              className="hidden dark:block absolute left-1/3 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2 md:col-span-2">
          <Label>Description</Label>
          <textarea
            name="description"
            placeholder="Enter Description"
            className="w-full p-2 border placeholder:text-sm rounded bg-background"
            rows={3}
            value={form.description}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={reset}
          className="border px-4 py-2 rounded-lg"
        >
          Reset
        </button>

        <button
          type="submit"
          disabled={creating || updating}
          className="bg-black text-white dark:bg-white dark:text-black px-4 py-2 rounded-lg"
        >
          {creating || updating ? (
            <Spinner className="w-5 h-5" />
          ) : editingAssignment ? (
            "Update"
          ) : (
            "Save"
          )}
        </button>
      </div>
    </form>
  );
}
